import { Timestamp } from 'firebase/firestore';
import { IGatsbyImageData, ImageDataLike } from 'gatsby-plugin-image';
import { MutableRefObject } from 'react';

export interface RecordingMarkerData {
  id: number;
  title: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  duration: number;
  recordist: number[] | null;
  habitat: number | null;
  mood: number | null;
  region: string | null;
  predominantSound: number | null;
  isLivestream: boolean;
  isMeditation: boolean | null;
  modified: string | null;
}

export interface RecordistType {
  id: number;
  graphqlId?: string;
  title: string | null;
  uri: string | null;
}

export interface RecordingPreviewData {
  id: number;
  title: string;
  userFriendlyTitle: string | null;
  uri: string;
  audio: string;
  isLivestream: boolean;
  isMeditation: boolean | null;
  isRadio: boolean | null;
  duration: number;
  recordists: RecordistType[];
  featuredImage: ImageDataLike | null;
  location: string | null;
  habitat: string | null;
  mood: string | null;
  predominantSound: string | null;
  isFree: boolean | null;
}

export interface PlaybackItem {
  id: number;
  title: string;
  userFriendlyTitle: string | null;
  uri: string;
  sourceUrl: string;
  coverImg: ImageDataLike | string | null;
  duration: number;
  isLivestream: boolean;
  isMeditation: boolean | null;
  isRadio: boolean | null;
  recordists: RecordistType[];
  recordist?: string; // Legacy field
  recordistUri?: string | null; // Legacy field
  isFree: boolean | null;
}

export type PlaylistItem = PlaybackItem & {
  habitat: string | null;
  location: string | null;
};

export interface Podcast {
  id: number;
  title: string;
  excerpt: string;
  uri: string;
  featuredImage: ImageDataLike | null;
  featuredImageStyle: string | null;
  isInterviewEpisode?: boolean;
}

export interface Playlist {
  id: string;
  title: string;
  userFriendlyTitle: string | null;
  subtitle: string | null;
  uri: string;
  thumbnail: IGatsbyImageData | string | null;
  owner: {
    id: string;
    name: string;
  };
  items: PlaylistItem[];
  public: boolean;
  playlistName: string | null;
  isTrendingRecordingsPlaylist?: boolean;
  /**
   * True when this playlist is a radio station and its items are that station's channels. The
   * player needs to tell the two apart: a channel's own title carries no station theme keyword
   * ("Leafy Rainfall"), only the station's does ("Rain FM"), so `title` here is the theme source.
   */
  isStationPlaylist?: boolean;
  isFree?: boolean | null;
}

export interface MemberPlaylist {
  id: string;
  name: string;
  description?: string | null;
  thumbnail?: string | null;
  public: boolean;
  owner: string;
  items?: number[];
}

export type AudioPlayerState = 'buffering' | 'playing' | 'paused' | 'idle' | 'error';

export interface AudioPlayerInstance {
  audioRef: MutableRefObject<HTMLAudioElement | undefined> | null;
  gainNodeRef: MutableRefObject<GainNode | null> | null;
  userVolumeRef: MutableRefObject<number> | null;
  state: AudioPlayerState;
  playlist: Playlist | null;
  currentItem: PlaybackItem | null;
  hasNext: boolean;
  hasPrev: boolean;
  currentTime: number;
  isShuffling: boolean;
  isLooping: boolean;
  isPreviewMode: boolean;
  previewTimeRemaining: number;
  isPlaybackFromPortfolio: boolean;
  startPlayback: (params: {
    item: PlaybackItem;
    playlist?: Playlist;
    eventSource?: string;
  }) => void;
  loadPlayback: (params: { item: PlaybackItem; eventSource?: string }) => void;
  play: () => void;
  pause: () => void;
  reset: () => void;
  seekTo: (time: number) => void;
  playPrevious: () => void;
  playNext: () => void;
  toggleIsShuffling: () => void;
  toggleIsLooping: () => void;
}

export interface StandaloneAudioPlayerType {
  isStandaloneAudioPlayerPlaying: boolean;
  setIsStandaloneAudioPlayerPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  currentlyPlayingItem: string;
  setCurrentlyPlayingItem: React.Dispatch<React.SetStateAction<string>>;
}

export interface FeaturedItemData {
  label: string;
  title: string | null;
  uri: string | null;
  description: string | null;
  date: string | null;
  featuredImage: ImageDataLike | null;
  featuredImageStyle: string | null;
}

export enum Membership {
  Business = 'business',
  Individual = 'individual',
  Family = 'family',
  None = 'none',
  // Below are the legacy membership plans
  // keeping it for the existing members
  Friend = 'friend',
  Supporter = 'supporter',
  Partner = 'partner',
}

export interface MemberProfileData {
  id: string; // firebase database id, same as firebase auth user.uid
  name: string;
  photoURL: string;
  memberSince: Timestamp;
  isArtist?: boolean;
}

export type LatestMember = Omit<MemberProfileData, 'memberSince'> & {
  memberSince: Date;
};

export interface Favorites {
  recordings?: number[];
  playlists?: string[];
  podcasts?: number[];
}

export interface SearchRecordist {
  title: string | null;
  uri: string | null;
}

export interface SearchRecord {
  id: string;
  uri: string | null;
  title: string | null;
  date: string | null;
  dateTimestamp: number | null;
  meta: Record<string, string | number | boolean | null | string[] | SearchRecordist[]>;
  type: string;
  image: IGatsbyImageData | null;
  isFree?: boolean | null;
}

type RecordistSocialLinks = {
  twitter: string | null;
  youtube: string | null;
  soundCloud: string | null;
  spotify: string | null;
  bandcamp: string | null;
  xenoCanto: string | null;
  bluesky: string | null;
};

export interface Recordist {
  id: string;
  uri: string;
  title: string;
  profileImage: ImageDataLike | null;
  backgroundImage: ImageDataLike | null;
  websiteUrl: string | null;
  date: string | null;
  socialLinks: RecordistSocialLinks | null;
  about: string | null;
}

export interface PlaylistData {
  id: string;
  title: string;
  userFriendlyTitle: string | null;
  playlistName: string | null;
  subtitle: string | null;
  uri: string;
  thumbnail: IGatsbyImageData | string | null;
}

export interface Recording {
  id: number | null;
  uri: string | null;
  title: string | null;
  recordingSettings: {
    recordists: Readonly<(RecordistType | null)[]> | null;
    audioUrl: string | null;
    audio: { mediaDetails: { length: number | null } | null } | null;
    region: string | null;
    habitat: { name: string | null } | null;
    isLivestream: boolean | null;
    isMeditation: boolean | null;
    userFriendlyTitle: string | null;
    isFree: boolean | null;
  } | null;
  featuredImage: {
    node: {
      localFile: { childImageSharp: { gatsbyImageData: IGatsbyImageData | null } | null } | null;
    } | null;
  } | null;
  popularity: number | null;
}

export type ReadonlyRecording = Readonly<Recording[]>;

export type LatestItem = {
  [key: string]: any;
  date?: string | null;
  memberSince?: Date;
};

export type TestimonialType = {
  author: string;
  content: string;
  country: string | null;
  featuredImage: ImageDataLike | undefined;
};

export type ScholarshipId = 'scholarship50' | 'scholarship60' | 'scholarship70' | 'scholarship100';

export type PromoCodeProps = {
  id: string;
  code: string;
};

export type PageContext = {
  currentPage: number;
  numPages: number;
};

export interface PaginationProps {
  currentPage: number;
  numPages: number;
  pathPrefix?: string;
  onPageChange?: (page: number) => void;
  className?: string;
}

export type EmbeddablePlaylistData = {
  id: number | null;
  title: string | null;
  description: string | null;
  recordings: PlaylistItem[];
};

export interface CheckoutSessionProps {
  metadata: {
    promo_type: ScholarshipId | null;
  } | null;
  price: string | null; // price id
}

export interface SubscriptionPricingType {
  amount: number; // Amount in dollars, after discount, before tax
  currency: string; // e.g., 'usd'
  interval: string | null; // Billing interval ('month', 'year', or null for lifetime/one-time)
  discount?: {
    percentage?: number; // Discount percentage (e.g., 50 for 50% off) - used for scholarship/coupon
    type: 'scholarship' | 'coupon' | 'prorated_credit'; // Type of discount
    originalAmount: number; // Original amount before discount in dollars
    creditAmount?: number; // Credit amount in dollars - used for prorated_credit
  };
}

export interface SubscriptionInfoType {
  membership: string | null;
  provider: 'apple.com' | 'stripe.com' | 'google.com' | 'unknown';
  ownershipType: 'PURCHASED' | 'FAMILY_SHARED' | 'UNKNOWN';
  memberLimit?: number;
  pricing?: SubscriptionPricingType;
  hasAppleFamilySharing?: boolean;
  isTrial?: boolean;
}

export interface SharedMemberData {
  id: string;
  name: string;
  email: string | null;
  photoURL?: string;
  joinedAt?: Timestamp | null;
}

interface ModalButton {
  content: string;
  url: string;
}

export interface InviteModal {
  id: string;
  title: string;
  primaryButton: ModalButton;
  secondaryButtons: ModalButton[] | [];
  content: string;
}
